import type { Editor } from '../editor/editor';
import type { History } from '../editor/history';
import { renderThumbnail } from '../export/render';
import { api, ApiError, type TemplateCategory, type TemplateInfo } from '../sync/api';

/** the server's codes for "the template is not the one this panel showed" (worker/rules.ts templateTargetProblem) */
const TEMPLATE_STALE = ['template_gone', 'template_changed', 'template_exists'];
import type { Saver } from '../sync/saver';
import { thDateTime } from '../time';
import { $ } from './dom';
import { openSheet } from './sheets';
import { toast } from './toast';

/*
 * Admins, and members given the right 'templates' (0011): publish one's own design as a template, or update it.
 * Hiding (and deleting, categories) stays with admins: the hide button shows only for them.
 * A template is a snapshot of the design as saved on the server at that moment, so the design is
 * first brought up to date on the server; if that isn't possible the reason is shown and nothing is published.
 *
 * A video work (เทมเพลตวิดีโอ): the server turns every clip into an empty slot of the same length (names from
 * "ช่องคลิป"), without the music. Its picture is the frame at the playhead with its text; after publishing, an MP4
 * (the last export, or a file) can be added as the template's preview video.
 */
export interface TemplateVideo { poster: () => Promise<string>; lastExport: () => Blob | null }

export function initSaveTemplateSheet(ed: Editor, history: History, saver: Saver, opts: { isAdmin: () => boolean; video: () => TemplateVideo | null }) {
  const nameIn = $('stName') as HTMLInputElement, catSel = $('stCat') as HTMLSelectElement;
  const thumbImg = $('stThumb') as HTMLImageElement, msg = $('stMsg');
  const publishBtn = $('stPublish') as HTMLButtonElement, hideBtn = $('stHide') as HTMLButtonElement;
  let current: TemplateInfo | null = null;
  let thumb = '';
  let busy = false;

  const errText = (e: unknown) => e instanceof Error ? e.message : String(e);
  const say = (text: string, ok = false) => { msg.textContent = text; msg.hidden = !text; msg.classList.toggle('ok', ok); };

  function paint() {
    const v = opts.video();
    $('stVideoNote').hidden = !v;
    $('stPrevRow').hidden = !v || !current;
    if (v && current) {
      $('stPrevInfo').textContent = current.preview ? 'มีวิดีโอตัวอย่างแล้ว (เลือกใหม่เพื่อเปลี่ยน)' : 'ยังไม่มีวิดีโอตัวอย่าง สมาชิกจะเห็นแค่รูป';
      ($('stPrevLast') as HTMLButtonElement).disabled = busy || !v.lastExport();
      ($('stPrevPick') as HTMLButtonElement).disabled = busy;
    }
    publishBtn.textContent = current ? 'อัปเดตเทมเพลต' : 'เผยแพร่เป็นเทมเพลต';
    hideBtn.hidden = !current || !opts.isAdmin();
    hideBtn.textContent = current?.hidden ? 'แสดงให้สมาชิกเห็น' : 'ซ่อน';
    $('stInfo').textContent = current
      ? `เทมเพลตนี้${current.hidden ? 'ซ่อนอยู่ สมาชิกไม่เห็น · ' : ''}อัปเดตล่าสุด ${thDateTime(new Date(current.updatedAt))}${current.updatedByName ? ` โดย ${current.updatedByName}` : ''} · ใช้ไป ${current.uses} ครั้ง`
      : 'งานนี้ยังไม่เป็นเทมเพลต';
    publishBtn.disabled = hideBtn.disabled = busy || !thumb;
  }

  function fillCategories(cats: TemplateCategory[], selected?: string) {
    catSel.textContent = '';
    for (const c of cats) catSel.append(new Option(c.name, c.id, false, c.id === selected));
  }

  async function open() {
    say(''); current = null; thumb = ''; thumbImg.removeAttribute('src');
    openSheet('shSaveTpl');
    paint();
    // the template is made from the server copy: get this design's edits up there first
    const why = await saver.flush();
    if (why || !saver.link.id) {
      say(`ยังบันทึกเป็นเทมเพลตไม่ได้: ${why ?? 'งานนี้ยังไม่เคยขึ้นเซิร์ฟเวอร์ แก้ไขสักอย่างให้ระบบบันทึกขึ้นก่อน'}`);
      return;
    }
    try {
      const [cats, t] = await Promise.all([api.templates(), api.designTemplate(saver.link.id)]);
      current = t.template;
      fillCategories(cats.categories, current?.categoryId);
      nameIn.value = current?.name ?? ed.doc.name;
    } catch (e) { say('โหลดข้อมูลเทมเพลตไม่สำเร็จ: ' + errText(e)); return; }
    try {
      const v = opts.video();
      // the gallery shows page 1, at page 1's size (the open page may be another size: หน้าหลายขนาด)
      const one = history.pageSize(0);
      thumb = v ? await v.poster() : await renderThumbnail(history.snapshot(), { ...ed.doc, ...one });
      thumbImg.src = thumb;
      $('stSize').textContent = `${one.w} × ${one.h} px${history.mixed() ? ' (หน้าแรก · หน้าอื่นมีขนาดต่างกัน)' : ''}`;
    } catch (e) { say('สร้างรูปตัวอย่างไม่สำเร็จ: ' + errText(e)); }
    paint();
  }

  publishBtn.onclick = async () => {
    if (!saver.link.id || busy) return;
    busy = true; paint(); say('');
    try {
      const why = await saver.flush();
      if (why) { say(`ยังบันทึกเป็นเทมเพลตไม่ได้: ${why}`); return; }
      const r = await api.publishTemplate(saver.link.id, { name: nameIn.value, categoryId: catSel.value, thumb, baseVersion: saver.link.version, templateId: current?.id ?? null });
      current = r.template;
      const text = (r.created ? `เผยแพร่ "${r.template.name}" เป็นเทมเพลตแล้ว สมาชิกเห็นในแท็บเทมเพลต` : `อัปเดตเทมเพลต "${r.template.name}" แล้ว`)
        + (!r.created && r.template.preview ? ' · วิดีโอตัวอย่างยังเป็นอันเดิม ถ้าแก้ช่องหรือข้อความ ให้ส่งออกใหม่แล้วกด "ใช้วิดีโอที่ส่งออกล่าสุด"' : '');
      say(text, true); toast(text);
    } catch (e) {
      say('บันทึกเป็นเทมเพลตไม่สำเร็จ: ' + errText(e));
      // the template changed elsewhere (deleted, or published from another device): show what is there now, so the
      // button says what the next press really does — a deleted template is only made again by "เผยแพร่"
      if (e instanceof ApiError && e.status === 409 && TEMPLATE_STALE.includes(String(e.body.code))) {
        try { current = (await api.designTemplate(saver.link.id)).template; }
        catch (e2) { say('บันทึกเป็นเทมเพลตไม่สำเร็จ: ' + errText(e) + ' · โหลดข้อมูลเทมเพลตใหม่ไม่สำเร็จ: ' + errText(e2) + ' ปิดแล้วเปิดหน้านี้ใหม่'); }
      }
    } finally { busy = false; paint(); }
  };

  hideBtn.onclick = async () => {
    if (!current || busy) return;
    busy = true; paint(); say('');
    const hide = !current.hidden;
    try {
      await api.admin.patchTemplate(current.id, { hidden: hide });
      current = { ...current, hidden: hide };
      say(hide ? 'ซ่อนเทมเพลตแล้ว สมาชิกจะไม่เห็นจนกว่าจะกดแสดง' : 'แสดงเทมเพลตให้สมาชิกเห็นแล้ว', true);
    } catch (e) { say((hide ? 'ซ่อน' : 'แสดง') + 'เทมเพลตไม่สำเร็จ: ' + errText(e)); }
    finally { busy = false; paint(); }
  };

  /* ---------- preview video of a video template ---------- */
  async function uploadPreview(mp4: Blob) {
    if (!saver.link.id || busy) return;
    if (mp4.type && mp4.type !== 'video/mp4') { say('วิดีโอตัวอย่างต้องเป็นไฟล์ MP4 (ส่งออกจากหน้าวิดีโอ)'); return; }
    busy = true; paint(); say(`กำลังอัปโหลดวิดีโอตัวอย่าง ${(mp4.size / 1048576).toFixed(1)} MB…`);
    try {
      const r = await api.uploadTemplatePreview(saver.link.id, mp4.type ? mp4 : new Blob([mp4], { type: 'video/mp4' }));
      current = { ...r.template, updatedByName: r.template.updatedByName };
      say('ใส่วิดีโอตัวอย่างแล้ว สมาชิกกดดูได้ก่อนใช้เทมเพลต', true);
    } catch (e) { say('อัปโหลดวิดีโอตัวอย่างไม่สำเร็จ: ' + errText(e)); }
    finally { busy = false; paint(); }
  }
  $('stPrevLast').onclick = () => { const b = opts.video()?.lastExport(); if (b) void uploadPreview(b); else say('ยังไม่มีวิดีโอที่ส่งออกจากงานนี้ในแบบปัจจุบัน (แก้งานหลังส่งออก) กด "ส่งออก" ใหม่ก่อน'); };
  const prevFile = $<HTMLInputElement>('stPrevFile');
  $('stPrevPick').onclick = () => { prevFile.value = ''; prevFile.click(); };
  prevFile.onchange = () => { const f = prevFile.files?.[0]; if (f) void uploadPreview(f); };

  return { open };
}
